import { isUndefined } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatInTimeZone } from 'domains/dates/format';
import { Container } from 'components/Container';
import { DocumentTitle } from 'components/DocumentTitle';
import { useTrack } from 'utils/hooks/useMixPanel';
import { Loader } from 'components/Loader';
import { RescheduleMixpanelEvents } from 'domains/direct-reschedule/mixpanel';
import { TimeSlotPicker } from 'components/TimeSlotPicker/TimeSlotPicker';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { useModal } from 'utils/hooks/useModal';
import config from 'config';
import { Heading, Paragraph } from 'components/Typography';
import { DATE_LONG_DAY_MONTH_YEAR, TIME_ONLY } from 'utils/date';
import { useRescheduleState } from 'domains/direct-reschedule/useRescheduleProvider';
import { FlashMessage } from 'components/FlashMessages';
import { getFirstAvailableDayIndex } from 'domains/consultations/freeConsultationSchedule';
import { PhoneNumberSection } from 'domains/appointments/reschedule/PickRescheduleDate';
import { getMaxRescheduleDate } from 'domains/appointments/reschedule/calendar';
import { PaginatedDayPicker } from './blocks/PaginatedDayPicker';

type RescheduleProps = {
  loading?: boolean;
  isError?: boolean;
};

const RescheduleUI = ({ loading = false, isError }: RescheduleProps) => {
  useTrack(RescheduleMixpanelEvents.ViewedReschedule);

  const { t } = useTranslation();
  const { openModal: openErrorModal, modalProps: errorModalProps } = useModal({ dismissable: false });
  const navigator = useNavigate();
  const { displayTimezone } = useDisplayTimezone();
  const {
    timeSlot,
    setTimeSlot,
    clientId,
    rescheduleId,
    availableTimes,
    rescheduleInformation,
    rescheduleQuery,
    availableTimesQuery,
  } = useRescheduleState();

  const [selectedDayIndex, setSelectedDayIndex] = useState<number | undefined>();

  /** @note(juan): this sync is terrible but only way to do it without major refactors to the context
   * This pre-selects the first day with slots available once the initial query is done
   */
  useEffect(() => {
    if (availableTimesQuery.isSuccess && isUndefined(selectedDayIndex)) {
      setSelectedDayIndex(getFirstAvailableDayIndex(availableTimes));
    }
  }, [availableTimesQuery.isSuccess]);

  const currentSlots = isUndefined(selectedDayIndex) ? undefined : availableTimes[selectedDayIndex].availableTimes;
  const onChangeDay = useCallback(
    (newDayIndex: number) => {
      setSelectedDayIndex(newDayIndex);
      setTimeSlot(undefined);
    },
    [setSelectedDayIndex, setTimeSlot],
  );

  useEffect(() => {
    if (isError && rescheduleQuery.isFetched) openErrorModal();
  }, [isError, openErrorModal, rescheduleQuery.isFetched]);
  return (
    <>
      <DocumentTitle>{t('reschedule.pageTitle')}</DocumentTitle>
      <Container className="bg-cream-50 min-h-full max-w-[40rem] md:px-0 flex flex-col items-center" size="full">
        {loading && (
          <Loader
            data-testid="billing-loader"
            className="flex grow items-center justify-center p-24 text-primary-500"
          />
        )}
        {!loading && !isError && (
          <div>
            <Heading level="h2" className="mb-16">
              {t('reschedule.mainHeading', {
                date: formatInTimeZone(
                  new Date(rescheduleInformation?.originalAcuityAppointment?.datetime ?? ''),
                  displayTimezone?.value ?? '',
                  DATE_LONG_DAY_MONTH_YEAR,
                ),

                time: formatInTimeZone(
                  rescheduleInformation?.originalAcuityAppointment.datetime ?? '',
                  displayTimezone?.value ?? '',
                  TIME_ONLY,
                ),
                timezone: displayTimezone?.shortName,
              })}
            </Heading>

            <Paragraph className="mb-64">
              {t('reschedule.subHeading', {
                timezone: displayTimezone?.shortName,
              })}
            </Paragraph>

            <FlashMessage variant="info" className="mb-64 sm-d:mb-40">
              {t('reschedule.infoCard')}
            </FlashMessage>

            {displayTimezone && (
              <>
                <PaginatedDayPicker
                  onChange={onChangeDay}
                  selectedDayIndex={selectedDayIndex}
                  maxDay={
                    rescheduleInformation &&
                    getMaxRescheduleDate(new Date(rescheduleInformation.originalAcuityAppointment.datetime))
                  }
                />

                {Boolean(currentSlots?.length) && (
                  <TimeSlotPicker onChange={setTimeSlot} timeSlots={currentSlots ?? []} value={timeSlot} />
                )}
              </>
            )}

            <PhoneNumberSection />

            <div className="flex flex-col md:flex-row w-full md:w-auto gap-y-[24px] md:gap-x-16">
              <Button
                variant="primary"
                onClick={() => {
                  navigator(`/reschedule/${clientId}/${rescheduleId}/confirm`);
                }}
                size="lg"
                disabled={isUndefined(timeSlot) || isUndefined(selectedDayIndex)}
              >
                {t('reschedule.submit')}
              </Button>
            </div>
          </div>
        )}
        <Modal {...errorModalProps}>
          <Heading level="h4" className="mb-16">
            {rescheduleInformation?.status && rescheduleInformation.status !== 'active'
              ? t('reschedule.confirm.rescheduleLinkExpired')
              : t('reschedule.confirm.appointmentNoLongerValidTitle')}
          </Heading>

          <Heading level="h7">
            {rescheduleInformation?.status && rescheduleInformation.status !== 'active'
              ? 't'
              : t('reschedule.cantFindAppointment.modal.expiredLink', {
                  supportEmail: config.get('supportEmail'),
                })}
            .
          </Heading>
          <Button size="lg" className="mt-40 w-full md:w-auto" as="a" href={`mailto:${config.get('supportEmail')}`}>
            {t('reschedule.cantFindAppointment.contactSupport')}
          </Button>
        </Modal>
      </Container>
    </>
  );
};

export default RescheduleUI;
